import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import SEO from '../components/SEO';

export default function WineLectures() {
  const { t, getRouteUrl } = useLanguage();
  const page = t.lecturesPage;

  return (
    <main className="bg-stone-50 pt-32">
      <SEO pageKey="lectures" />
      
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-[#f7f2ea] text-stone-900 border-b border-stone-200/80">
        <div className="grid-container">
          <div className="max-w-4xl">
            <span className="text-xs font-bold uppercase tracking-[0.4em] text-[#581c24] mb-4 block">
              {page.badge}
            </span>
            <h1 className="text-5xl md:text-7xl font-serif font-bold uppercase leading-tight tracking-tight mb-8 text-stone-950">
              {page.heroTitle1} <br /> 
              <span className="text-[#581c24] italic font-light">{page.heroTitle2}</span>
            </h1>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start mt-8">
            <div className="lg:col-span-8">
              <div className="aspect-video rounded-xl overflow-hidden shadow-lg border border-stone-200 relative group">
                <img 
                  src="https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/178dabb9-6b5e-454d-ad5e-6b83fa377544.jpg" 
                  alt={page.imageAlt} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" 
                />
              </div>
            </div>
            <div className="lg:col-span-4 space-y-6">
              <div className="p-8 bg-white text-stone-900 rounded-xl border border-stone-200 shadow-sm">
                <h3 className="text-lg font-serif font-bold uppercase tracking-tight mb-3 text-[#581c24]">
                  {page.essenceTitle}
                </h3>
                <p className="text-xs text-stone-700 leading-relaxed font-normal">
                  {page.essenceText}
                </p>
              </div>
              <Link to={getRouteUrl('contact')} className="btn-primary w-full text-center block">
                {page.bookButton}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24 bg-[#faf8f5]">
        <div className="grid-container">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20">
            <div className="md:col-span-5 space-y-8">
              <h2 className="text-3xl md:text-4xl font-serif font-bold uppercase tracking-tight text-stone-950">
                {page.contentTitle}
              </h2>
              <div className="space-y-6">
                {page.topics.map((topic, idx) => (
                  <div key={idx} className="flex space-x-6 pb-6 border-b border-stone-200/80 last:border-0">
                    <span className="text-2xl font-serif font-bold italic text-[#581c24]">0{idx + 1}</span>
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-widest text-stone-950 mb-1">{topic.title}</h4>
                      <p className="text-xs text-stone-600 leading-relaxed">{topic.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-7 space-y-8">
              <div className="p-8 bg-[#f4efe6] rounded-xl border border-stone-200 shadow-sm">
                <blockquote className="text-xl text-[#581c24] font-serif italic leading-relaxed">
                  "{page.quote}"
                </blockquote>
              </div>
              <div className="text-stone-700 space-y-4 leading-relaxed text-sm md:text-base">
                <p>
                  {page.paragraph1}
                </p>
                <p>
                  {page.paragraph2}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square rounded-xl overflow-hidden border border-stone-200 shadow-sm">
                  <img src="https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/4b48daac-7874-4af2-bb70-1c8c51ac136e.jpg" alt="Wine Details" className="w-full h-full object-cover" />
                </div>
                <div className="aspect-square rounded-xl overflow-hidden border border-stone-200 shadow-sm">
                  <img src="https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/44120024-e095-446b-8ee7-1dea28fabce6.jpg" alt="Wine Pour" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#f2ede4] border-t border-stone-200 text-center">
        <div className="grid-container max-w-3xl space-y-8">
          <h2 className="text-4xl md:text-6xl font-serif font-bold uppercase tracking-tight text-stone-950">
            {page.ctaTitle1} <br /> 
            <span className="text-[#581c24] italic font-light">{page.ctaTitle2}</span>
          </h2>
          <div>
            <Link to={getRouteUrl('contact')} className="btn-primary">
              {page.ctaButton} <ArrowRight className="inline-block ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}
